import {
  Directive,
  inject,
  InjectionToken,
  OnDestroy,
  Type,
} from '@angular/core';
import { camelCase, pull, snakeCase, upperFirst } from '@sama/micro-dash';
import { InjectableSuperclass } from '@sama/ng-core';
import { SubscriptionManager } from '@sama/rxjs-core';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { EventTrackingValue } from '../../../team-apis/core/event-tracking';
import { shellConnectorToken } from '../../../team-apis/core/shell-connector';
import {
  ShapeType,
  VectorTaggingShapeType,
} from '../../../team-apis/otterdam/answers/shape-typings';
import {
  InteractiveBorderEventInfo,
  MouseEventFilters,
  MouseEventInfo,
  MouseEventService,
  MouseEventType,
  ShapeEventInfo,
  SubpointEventInfo,
} from '../../misc-services/events/mouse-event.service';
import { KeyboardShortcutName } from '../../misc-services/keyboard-shortcut.service';
import { AbstractSensorContentState } from '../../state/abstract-sensor-content-state';
import { AbstractSensorContentStore } from '../../state/abstract-sensor-content-store';
import {
  pointCloudShapeTools,
  ToolName,
  vectorTaggingShapeTools,
  VectorTaggingToolObjectless,
} from '../../tools-names';
import { AbstractToolHelper } from './tool-helpers/abstract-tool-helper';

export const TOOLS_TOKEN = new InjectionToken<AbstractTool[]>('TOOLS_TOKEN');
export const dragMoveTestHook = Symbol();

interface ToolEventFilters extends MouseEventFilters {
  evenWithRegisteredHelper?: boolean;
}

export function makeToolProviders(...subclasses: Array<Type<any>>) {
  return [
    subclasses,
    subclasses.map((subclass) => ({
      provide: TOOLS_TOKEN,
      useExisting: subclass,
      multi: true,
    })),
  ];
}

export function toolNameToShapeType(
  toolName: VectorTaggingToolObjectless,
): VectorTaggingShapeType;
export function toolNameToShapeType(toolName: ToolName): ShapeType;
export function toolNameToShapeType(toolName: ToolName): ShapeType {
  if (toolName === 'Keypoint') {
    return 'keypoint_shape';
  } else {
    return snakeCase(toolName) as ShapeType;
  }
}

export function shapeTypeToToolName(shapeType: ShapeType): ToolName {
  if (shapeType === 'keypoint_shape') {
    return 'Keypoint';
  } else {
    return upperFirst(camelCase(shapeType)) as ToolName;
  }
}

export function toolNameForEventTracking(toolName: ToolName) {
  return snakeCase(toolName);
}

export function getShortcutName(toolName: ToolName) {
  return `select${toolName}Tool` as KeyboardShortcutName;
}

@Directive()
export abstract class AbstractTool<
    A extends AbstractSensorContentState = AbstractSensorContentState,
    T extends AbstractSensorContentStore<A> = AbstractSensorContentStore<A>,
  >
  extends InjectableSuperclass
  implements OnDestroy
{
  isInitialized = false;
  secondaryToolbarType?: Type<any>;
  shortcutName!: string;
  activated$ = new BehaviorSubject(false);

  protected mouseEventService = inject(MouseEventService);
  protected shellConnector = inject(shellConnectorToken);
  protected store = inject(AbstractSensorContentStore) as T;

  private helpers: AbstractToolHelper[] = [];
  private activeSubscriptionManager = new SubscriptionManager();
  abstract readonly name: ToolName;

  ngOnDestroy() {
    this.activeSubscriptionManager.unsubscribe();
    this.helpers.forEach((helper) => helper.unsubscribe());
  }

  onInit() {
    this.isInitialized = true;
    this.shortcutName = getShortcutName(this.name);
  }

  async onActivate(): Promise<void | ImageData> {
    this.setSelectedTool();
    this.activated$.next(true);
  }

  onDeactivate() {
    this.onCancel({ source: 'tool_deactivated' });
    this.activeSubscriptionManager.unsubscribe();
    this.activated$.next(false);
  }

  onCancel(eventTrackingValue: EventTrackingValue) {
    for (const helper of this.helpers) {
      helper.cancel(eventTrackingValue);
    }
  }

  hasRegisteredHelper() {
    return this.helpers.length > 0;
  }

  setMode(_mode: string) {}

  protected onEvent(
    target: 'container',
    type: MouseEventType,
    handler: (event: MouseEvent, eventInfo: MouseEventInfo) => void,
    filters?: ToolEventFilters,
  ): void;

  protected onEvent(
    target: 'shape',
    type: MouseEventType,
    handler: (event: MouseEvent, eventInfo: ShapeEventInfo) => void,
    filters?: ToolEventFilters,
  ): void;

  protected onEvent(
    target: 'subpoint',
    type: MouseEventType,
    handler: (event: MouseEvent, eventInfo: SubpointEventInfo) => void,
    filters?: ToolEventFilters,
  ): void;

  protected onEvent(
    target: 'interactiveBorder',
    type: MouseEventType,
    handler: (event: MouseEvent, eventInfo: InteractiveBorderEventInfo) => void,
    filters?: ToolEventFilters,
  ): void;

  protected onEvent(
    target: any,
    type: MouseEventType,
    handler: (event: MouseEvent, eventInfo: any) => void,
    filters: ToolEventFilters = {},
  ) {
    let event$ = this.mouseEventService.getEvent$(target, type, filters);
    if (!filters.evenWithRegisteredHelper) {
      event$ = event$.pipe(filter(() => !this.hasRegisteredHelper()));
    }

    const tools = [
      ...vectorTaggingShapeTools,
      ...pointCloudShapeTools,
    ] as readonly string[];
    this.subscribeToWhileActive(event$, (info) => {
      const { selectedTool, shapeCreationEnabled } = this.store.state();

      if (!shapeCreationEnabled && tools.includes(selectedTool || '')) {
        return;
      }

      handler.call(this, info.event, info);
    });
  }

  protected subscribeToWhileActive<O>(
    observable$: Observable<O>,
    next: (value: O) => void,
  ) {
    this.activeSubscriptionManager.subscribeTo(observable$, next.bind(this));
  }

  protected registerHelper(helper: AbstractToolHelper) {
    this.helpers.push(helper);
    void helper.completion.finally(() => {
      pull(this.helpers, helper);
    });
    helper.startListening();
  }

  protected setSelectedTool() {
    this.shellConnector.setSelectedTool(this.name);
  }
}
